/**
 * Query extractors — project rows from `PLANT_QUERIES_CYPHER` and
 * `PLANT_QUERY_DETAIL_CYPHER` into the typed `PlantQuerySummary` /
 * `PlantQueryDetail` shapes the Plant Viewer's Queries tab renders.
 *
 * A "query" is one of two graph flavors (see `PlantQueryKind`): a
 * `:NamedQuery` node or a query-flavored `:Tag` (`tag_system='db'` or
 * `ignition_tag_type='query'`). The two carry the same information
 * under different property names (SQL under `query_text` vs `query`;
 * datasource under `db_name` vs `datasource`), so these helpers
 * normalize before the UI reads them.
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractNode, toIntCount } from '../graph-row'
import { compareByNameThenId } from '../sort'
import { normalizeComputeHost } from './compute'
import type {
  CypherReadResult,
  PlantComputeHost,
  PlantDatabase,
  PlantDatabaseRef,
  PlantQueryDetail,
  PlantQueryFolderNode,
  PlantQueryHostRef,
  PlantQueryKind,
  PlantQueryNode,
  PlantQueryParameter,
  PlantQuerySummary,
  PlantQueryTableRef
} from '../types'

/** Normalize a cypher `kind` cell to a `PlantQueryKind` (defaults to `query_tag`). */
function coerceKind(value: unknown): PlantQueryKind {
  return value === 'named_query' ? 'named_query' : 'query_tag'
}

/**
 * Normalize a NamedQuery `folder_path` for tree grouping: trim, drop leading /
 * trailing slashes, and collapse empty segments. Returns `undefined` for the
 * project root (no folder).
 */
export function normalizeFolderPath(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const cleaned = value
    .split('/')
    .map((seg) => seg.trim())
    .filter((seg) => seg.length > 0)
    .join('/')
  return cleaned.length > 0 ? cleaned : undefined
}

/** The SQL text of a query, regardless of flavor. */
export function queryTextOf(node: PlantQueryNode): string | undefined {
  const text = node.query_text ?? node.query
  return typeof text === 'string' && text.trim().length > 0 ? text : undefined
}

/** The datasource / database display name of a query, regardless of flavor. */
export function databaseNameOf(
  node: PlantQueryNode,
  database?: PlantDatabaseRef
): string | undefined {
  const name = database?.name ?? node.db_name ?? node.datasource
  return typeof name === 'string' && name.trim().length > 0 ? name : undefined
}

/** Project a cypher `:Database` cell into a thin {@link PlantDatabaseRef}. */
export function extractDatabaseRef(value: unknown): PlantDatabaseRef | undefined {
  const node = extractNode<PlantDatabase>(value)
  if (!node) return undefined
  return {
    id: node.id,
    name: node.name,
    ...(node.database_type ? { database_type: node.database_type } : {}),
    ...(node.url ? { url: node.url } : {})
  }
}

/** Project a `collect(distinct tbl)` list into name-sorted {@link PlantQueryTableRef}s. */
export function extractTableRefs(value: unknown): PlantQueryTableRef[] {
  if (!Array.isArray(value)) return []
  const out: PlantQueryTableRef[] = []
  for (const item of value) {
    const node = extractNode<{ id: string; _id: string; name: string; table_schema?: string }>(item)
    if (!node) continue
    const schema = (node as unknown as Record<string, unknown>)['table_schema']
    out.push({
      id: node.id,
      name: node.name,
      ...(typeof schema === 'string' && schema ? { schema } : {})
    })
  }
  out.sort(compareByNameThenId)
  return out
}

/**
 * Coerce a NamedQuery's `columns` property into a flat `string[]`. Real
 * data has carried either bare column-name strings or `{ name }` objects
 * across ingest versions, so accept both and drop anything unnameable.
 */
export function coerceColumns(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const item of value) {
    if (typeof item === 'string' && item.trim()) {
      out.push(item)
    } else if (item && typeof item === 'object') {
      const name = (item as Record<string, unknown>)['name']
      if (typeof name === 'string' && name.trim()) out.push(name)
    }
  }
  return out
}

/** Coerce a NamedQuery's `parameters` property into typed {@link PlantQueryParameter}s. */
export function coerceParameters(value: unknown): PlantQueryParameter[] {
  if (!Array.isArray(value)) return []
  const out: PlantQueryParameter[] = []
  for (const item of value) {
    if (typeof item === 'string' && item.trim()) {
      out.push({ name: item })
    } else if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      const name = obj['name']
      if (typeof name !== 'string' || !name.trim()) continue
      const type = obj['type']
      out.push({ name, ...(typeof type === 'string' && type ? { type } : {}) })
    }
  }
  return out
}

/**
 * Project one row from `PLANT_QUERIES_CYPHER` into a typed
 * {@link PlantQuerySummary}. Returns `null` for the sentinel row where
 * the node is missing.
 */
export function mapPlantQuerySummaryRow(row: Record<string, unknown>): PlantQuerySummary | null {
  const node = extractNode<PlantQueryNode>(row['node'])
  if (!node) return null
  const kind = coerceKind(row['kind'])
  const database = extractDatabaseRef(row['database'])
  const projectName = typeof node.project_name === 'string' ? node.project_name : undefined
  const queryType = typeof node.query_type === 'string' ? node.query_type : undefined
  const queryName =
    typeof node.query_name === 'string' && node.query_name.trim() ? node.query_name : undefined
  const folderPath = normalizeFolderPath(node.folder_path)
  return {
    id: node.id,
    _id: node._id,
    name: node.name,
    kind,
    ...(queryTextOf(node) ? { queryText: queryTextOf(node) } : {}),
    ...(databaseNameOf(node, database) ? { databaseName: databaseNameOf(node, database) } : {}),
    ...(database ? { database } : {}),
    tableCount: toIntCount(row['tableCount']),
    ...(projectName ? { projectName } : {}),
    ...(queryType ? { queryType } : {}),
    ...(queryName ? { queryName } : {}),
    ...(folderPath ? { folderPath } : {}),
    ...(node.provenance ? { provenance: node.provenance } : {}),
    ...(node.valid != null ? { valid: node.valid } : {}),
    ...(node.confidence != null ? { confidence: node.confidence } : {})
  }
}

/** Project-root label for named queries missing a `project_name`. */
export const QUERY_TREE_UNGROUPED = 'Ungrouped'

function compareFolderName(a: { name: string }, b: { name: string }): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
}

function queryLeafLabel(q: PlantQuerySummary): string {
  return (q.queryName ?? q.name).toLowerCase()
}

/**
 * Group named-query summaries into a folder tree keyed on their Ignition
 * `project_name` (top level) then each segment of `folder_path`. Leaf queries
 * hang off the folder they live in; a query with no `folder_path` sits at its
 * project root. Pure + sorted (folders then queries, case-insensitive) so the
 * Queries tab renders a deterministic collapsible tree.
 */
export function buildQueryFolderTree(queries: PlantQuerySummary[]): PlantQueryFolderNode[] {
  const roots = new Map<string, PlantQueryFolderNode>()

  const ensureChild = (parent: PlantQueryFolderNode, name: string): PlantQueryFolderNode => {
    let child = parent.folders.find((f) => f.name === name)
    if (!child) {
      child = { name, path: `${parent.path}/${name}`, folders: [], queries: [] }
      parent.folders.push(child)
    }
    return child
  }

  for (const q of queries) {
    const projectName = q.projectName?.trim() || QUERY_TREE_UNGROUPED
    let root = roots.get(projectName)
    if (!root) {
      root = { name: projectName, path: projectName, folders: [], queries: [] }
      roots.set(projectName, root)
    }
    let cursor = root
    if (q.folderPath) {
      for (const segment of q.folderPath.split('/')) {
        cursor = ensureChild(cursor, segment)
      }
    }
    cursor.queries.push(q)
  }

  const sortNode = (node: PlantQueryFolderNode): void => {
    node.folders.sort(compareFolderName)
    node.queries.sort((a, b) => queryLeafLabel(a).localeCompare(queryLeafLabel(b)))
    node.folders.forEach(sortNode)
  }

  const list = Array.from(roots.values())
  list.sort(compareFolderName)
  list.forEach(sortNode)
  return list
}

const UNKNOWN_QUERY: PlantQueryNode = {
  id: '',
  _id: '',
  name: 'Unknown query'
}

/**
 * Project the single row returned by `PLANT_QUERY_DETAIL_CYPHER` into a
 * {@link PlantQueryDetail}. Falls back to a synthetic stub when the id
 * wasn't found so the page renders empty rather than crashing.
 */
export function mapPlantQueryDetail(raw: CypherReadResult, queryId: string): PlantQueryDetail {
  const row = raw.results[0] ?? {}
  const query = extractNode<PlantQueryNode>(row['node']) ?? {
    ...UNKNOWN_QUERY,
    id: queryId,
    _id: queryId
  }
  const kind = coerceKind(row['kind'])
  const database = extractDatabaseRef(row['database'])
  const hostNode = extractNode<PlantComputeHost>(row['host'])
  const host: PlantQueryHostRef | undefined = hostNode
    ? (() => {
        const normalized = normalizeComputeHost(hostNode)
        return {
          id: normalized.id,
          name: normalized.name,
          ...(normalized.compute_type ? { compute_type: normalized.compute_type } : {}),
          ...(normalized.url ? { url: normalized.url } : {})
        }
      })()
    : undefined
  return {
    query,
    kind,
    ...(queryTextOf(query) ? { queryText: queryTextOf(query) } : {}),
    ...(databaseNameOf(query, database) ? { databaseName: databaseNameOf(query, database) } : {}),
    ...(database ? { database } : {}),
    ...(host ? { host } : {}),
    tables: extractTableRefs(row['tables']),
    columns: coerceColumns(query.columns),
    parameters: coerceParameters(query.parameters)
  }
}
